import React, { useEffect, useState, useCallback } from 'react';
import { Activity, RefreshCw, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sprint C+ — Riya Health Check widget.
 * Pings /api/admin/riya/healthcheck and renders aggregate status + per-check latency.
 * Auto-refreshes every 5 min.
 */
const RiyaHealthCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/riya/healthcheck`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Health check failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const t = setInterval(fetchHealth, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchHealth]);

  const theme = (() => {
    const overall = data?.overall;
    if (overall === 'down') {
      return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700',
        sub: 'text-red-600', icon: XCircle, label: 'Down' };
    }
    if (overall === 'degraded') {
      return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700',
        sub: 'text-amber-600', icon: AlertTriangle, label: 'Degraded' };
    }
    return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700',
      sub: 'text-emerald-600', icon: CheckCircle, label: 'Healthy' };
  })();

  const Icon = theme.icon;

  return (
    <div
      className={`rounded-xl border ${theme.border} ${theme.bg} p-5 transition-shadow hover:shadow-md`}
      data-testid="riya-health-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className={`w-5 h-5 ${theme.text}`} />
          <h3 className="text-sm font-bold text-gray-800">
            Riya Health
            {data?.mock && (
              <span className="ml-2 text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                MOCK
              </span>
            )}
          </h3>
        </div>
        <button
          onClick={fetchHealth}
          disabled={loading}
          className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50 flex items-center gap-1"
          data-testid="riya-health-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Probe
        </button>
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-100 rounded p-2 mb-2">
          {typeof error === 'string' ? error : 'Health probe error'}
        </div>
      )}

      {!data && !error && <div className="text-xs text-gray-500">Probing…</div>}

      {data && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Icon className={`w-5 h-5 ${theme.sub}`} />
            <span className={`text-xl font-bold ${theme.text}`}>{theme.label}</span>
          </div>
          <div className="space-y-1.5">
            {(data.checks || []).map((c) => (
              <div key={c.name} className="flex items-center justify-between text-xs">
                <span className="capitalize text-gray-700 font-medium">{c.name.replace('_', ' ')}</span>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">{c.latency_ms}ms</span>
                  {c.status === 'ok' ? (
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 text-red-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-gray-400 mt-3">Auto-probe every 5 min</div>
        </>
      )}
    </div>
  );
};

export default RiyaHealthCard;
